const COMPLETED_STATES = new Set(["completed"]);

export function normalizeJob(id, value = {}) {
    const customer = value.customer || {};
    return {
        ...value,
        id: value.id || id,
        status: value.status || "received",
        collectionCode: value.collectionCode || value.id || id,
        createdAt: Number(value.createdAt) || 0,
        completedAt: Number(value.completedAt) || 0,
        filesDeleteAt: Number(value.filesDeleteAt) || 0,
        filesDeletedAt: Number(value.filesDeletedAt) || 0,
        customer: {
            ...customer,
            name: customer.name || "Customer",
            email: customer.email || "",
            phone: customer.phone || customer.cell || "",
        },
        files: Array.isArray(value.files) ? value.files : [],
    };
}

export function isCompletedJob(job) {
    return COMPLETED_STATES.has(String(job?.status || "").toLowerCase());
}

export function filterJobs(jobs, query = "", completed = false) {
    const needle = query.trim().toLowerCase();
    return (jobs || [])
        .filter((job) => isCompletedJob(job) === completed)
        .filter((job) => {
            if (!needle) return true;
            const haystack = [
                job.collectionCode,
                job.customer?.name,
                job.customer?.email,
                job.customer?.phone,
                job.note,
                ...(job.files || []).map((file) => file.name),
            ].join(" ").toLowerCase();
            return haystack.includes(needle);
        })
        .sort((left, right) => (right.createdAt || 0) - (left.createdAt || 0));
}

export function buildCounterUploadUrl(origin, shopCode) {
    if (!origin || !shopCode) return "";
    const siteOrigin = new URL(origin).origin;
    return `${siteOrigin}/marketplace?shopCode=${encodeURIComponent(String(shopCode))}`;
}

const startOfWeek = (date) => {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    const weekday = (result.getDay() + 6) % 7;
    result.setDate(result.getDate() - weekday);
    return result;
};

export function buildDashboardAnalytics(jobs = [], now = Date.now()) {
    const weeks = Array.from({ length: 12 }, (_, index) => {
        const start = startOfWeek(now);
        start.setDate(start.getDate() - (11 - index) * 7);
        return { start, count: 0 };
    });
    const arrivals = Array.from({ length: 7 }, () => Array(24).fill(0));
    const fileTypes = new Map();
    let filesTotal = 0;

    jobs.forEach((job) => {
        const received = new Date(Number(job.createdAt) || 0);
        if (Number.isNaN(received.getTime())) return;

        const weekStart = startOfWeek(received).getTime();
        const week = weeks.find((item) => item.start.getTime() === weekStart);
        if (week) week.count += 1;
        arrivals[(received.getDay() + 6) % 7][received.getHours()] += 1;

        (Array.isArray(job.files) ? job.files : []).forEach((file) => {
            filesTotal += 1;
            const extension = String(file.name || "").split(".").pop().toLowerCase();
            const type = extension && extension !== String(file.name || "").toLowerCase()
                ? `.${extension}`
                : "Other";
            fileTypes.set(type, (fileTypes.get(type) || 0) + 1);
        });
    });

    return {
        weeks: weeks.map(({ start, count }) => ({
            label: `${start.getDate()} ${start.toLocaleString("en-ZA", { month: "short" })}`,
            count,
        })),
        arrivals,
        fileTypes: [...fileTypes.entries()]
            .map(([type, count]) => ({ type, count }))
            .sort((left, right) => right.count - left.count),
        filesTotal,
        jobsTotal: jobs.length,
        completedTotal: jobs.filter(isCompletedJob).length,
        receivedTotal: jobs.filter((job) => !isCompletedJob(job)).length,
        customerTotal: new Set(jobs.map((job) => job.customer?.email).filter(Boolean)).size,
    };
}

export function normalizeEnquiry(id, value = {}) {
    return {
        ...value,
        id: value.id || id,
        customer: {
            name: value.userName || value.customer?.name || "Customer",
            email: value.userEmail || value.customer?.email || "",
            phone: value.userPhone || value.customer?.phone || "",
        },
        message: value.message || value.question || "",
        topics: Array.isArray(value.topics) ? value.topics : [],
        createdAt: Number(value.createdAt) || 0,
        closed: value.isClosed === true || value.state === "closed",
        read: value.isRead === true || value.state === "replied" || value.state === "closed",
    };
}
